import {
  ref, computed, watch, onBeforeUnmount, onMounted, onUnmounted,
} from 'vue';
import { useStore } from 'vuex';
// eslint-disable-next-line import-x/extensions
import { useAuth } from '@websanova/vue-auth/src/v3.js';
import axios from 'axios';
import moment from 'moment';
import { useI18n } from 'vue-i18n';
import { measuredPings } from '../utils/server_locations';
import {
  loadUserPreferences,
  matchmakingCsMapNames,
  matchmakingPlayModeId,
  matchmakingRegionId,
  preferencesLoaded,
  setMatchmakingMapNames,
  setMatchmakingPlayMode,
  setMatchmakingRegion,
} from '../utils/user_preferences';

const INACTIVE_LOBBY_STATUSES = ['completed', 'cancelled', 'forming'];
const SELECTION_SYNC_DELAY_MS = 300;

export function useQueueSearch() {
  const store = useStore();
  const auth = useAuth();
  const now = ref(moment());
  let timer = null;

  const party = computed(() => store.getters.matchmakingParty);
  const queueEntry = computed(() => store.getters.matchmakingQueueEntry);
  const isQueued = computed(() => !!queueEntry.value);
  const isOwner = computed(() => party.value?.owner_id === auth.user()?.id);
  const activeLobby = computed(() => {
    const lobby = store.getters.matchmakingLobby;
    return lobby && !INACTIVE_LOBBY_STATUSES.includes(lobby.status) ? lobby : null;
  });

  const elapsedLabel = computed(() => {
    if (!queueEntry.value) return '0:00';
    const seconds = Math.max(0, now.value.diff(moment(queueEntry.value.created_at), 'seconds'));
    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = String(seconds % 60).padStart(2, '0');
    return `${minutes}:${remainingSeconds}`;
  });

  function cancelMatchmaking() {
    if (!queueEntry.value) return;
    axios.delete(`/matchmaking/queue_entries/${queueEntry.value.id}`).then(() => {
      store.commit('setMatchmakingParty', { ...party.value, status: 'open', queue_entry: null });
    });
  }

  watch(isQueued, (queued) => {
    clearInterval(timer);
    timer = null;
    if (!queued) return;
    now.value = moment();
    timer = setInterval(() => {
      now.value = moment();
    }, 1000);
  }, { immediate: true });

  onUnmounted(() => {
    clearInterval(timer);
  });

  return {
    party, queueEntry, isQueued, isOwner, activeLobby, elapsedLabel, cancelMatchmaking,
  };
}

export function useMatchmakingQueue() {
  const { t } = useI18n();
  const store = useStore();
  const auth = useAuth();

  const playModes = ref([]);
  const regions = ref([]);
  const selectedPlayMode = ref(null);
  const selectedRegion = ref(null);
  const selectedMapNames = ref([]);
  const leaving = ref(false);
  const queueing = ref(false);
  const queueError = ref('');
  let selectionSyncTimer = null;

  const {
    party, queueEntry, isQueued, isOwner, activeLobby, elapsedLabel, cancelMatchmaking,
  } = useQueueSearch();

  const currentMembership = computed(
    () => party.value?.members?.find((m) => m.id === auth.user()?.id),
  );

  // Once queued, the queue entry is the only authoritative record of what the
  // party is searching for: the leader's saved selection can change from another
  // tab mid-queue, and members never had a local selection at all. Read the entry
  // back for both.
  const queuedPlayMode = computed(() => {
    const ids = queueEntry.value?.play_mode_ids || [];
    return playModes.value.find((playMode) => ids.includes(playMode.id)) || null;
  });
  const queuedRegion = computed(
    () => regions.value.find((region) => region.id === queueEntry.value?.region_id) || null,
  );
  const queuedMapNames = computed(() => queueEntry.value?.cs_map_names || []);

  const partyPlayMode = computed(
    () => playModes.value.find((playMode) => playMode.id === party.value?.play_mode_id) || null,
  );
  const partyRegion = computed(
    () => regions.value.find((region) => region.id === party.value?.region_id) || null,
  );
  const partyMapNames = computed(() => party.value?.cs_map_names || []);

  const selectorsLocked = computed(() => isQueued.value || !isOwner.value);
  const lockedReason = computed(() => (isOwner.value
    ? t('play.matchmaking.locked_while_queued')
    : t('play.matchmaking.locked_leader_only')));

  function allMapNames(playMode) {
    return playMode?.cs_maps?.map((csMap) => csMap.name) || [];
  }

  function restoreSelection() {
    const storedPlayMode = playModes.value.find(
      (playMode) => playMode.id === matchmakingPlayModeId.value,
    );
    const playMode = storedPlayMode || playModes.value[0] || null;
    const available = allMapNames(playMode);
    const storedMapNames = matchmakingCsMapNames.value.filter((name) => available.includes(name));
    const storedRegion = regions.value.find((region) => region.id === matchmakingRegionId.value);

    selectedPlayMode.value = playMode;
    selectedMapNames.value = storedMapNames.length > 0 ? storedMapNames : available;
    selectedRegion.value = storedRegion || regions.value[0] || null;
  }

  watch(preferencesLoaded, (loaded) => {
    if (loaded) restoreSelection();
  });

  function sameMapNames(a, b) {
    return a.length === b.length && [...a].sort().join() === [...b].sort().join();
  }

  function syncPartySelection() {
    selectionSyncTimer = null;
    const current = party.value;
    if (!preferencesLoaded.value || !selectedPlayMode.value || !selectedRegion.value) return;
    if (!isOwner.value || current?.status !== 'open') return;

    const playModeId = selectedPlayMode.value.id;
    const regionId = selectedRegion.value.id;
    if (
      current.play_mode_id === playModeId
      && current.region_id === regionId
      && sameMapNames(current.cs_map_names || [], selectedMapNames.value)
    ) return;

    axios
      .put(`/matchmaking/parties/${current.id}`, {
        party: {
          play_mode_id: playModeId,
          region_id: regionId,
          cs_map_names: selectedMapNames.value,
        },
      })
      .catch(() => {});
  }

  watch(
    [selectedPlayMode, selectedRegion, selectedMapNames, preferencesLoaded, isOwner,
      () => party.value?.id, () => party.value?.status],
    () => {
      clearTimeout(selectionSyncTimer);
      selectionSyncTimer = setTimeout(syncPartySelection, SELECTION_SYNC_DELAY_MS);
    },
  );

  // Writable so the leader's v-model still works when unlocked; the setters are
  // unreachable while locked because the dropdowns stop emitting.
  const shownPlayMode = computed({
    get: () => {
      if (isQueued.value) return queuedPlayMode.value;
      return isOwner.value ? selectedPlayMode.value : partyPlayMode.value;
    },
    set: (playMode) => {
      const changed = playMode?.id !== selectedPlayMode.value?.id;
      selectedPlayMode.value = playMode;
      if (changed) selectedMapNames.value = allMapNames(playMode);
      setMatchmakingPlayMode(playMode, selectedMapNames.value);
    },
  });
  const shownRegion = computed({
    get: () => {
      if (isQueued.value) return queuedRegion.value;
      return isOwner.value ? selectedRegion.value : partyRegion.value;
    },
    set: (region) => {
      selectedRegion.value = region;
      setMatchmakingRegion(region);
    },
  });
  const shownMapNames = computed({
    get: () => {
      if (isQueued.value) return queuedMapNames.value;
      return isOwner.value ? selectedMapNames.value : partyMapNames.value;
    },
    set: (mapNames) => {
      selectedMapNames.value = mapNames;
      setMatchmakingMapNames(mapNames);
    },
  });
  const shownMaps = computed(() => shownPlayMode.value?.cs_maps || []);

  const partySize = computed(() => party.value?.members?.length || 0);
  const partyLimit = computed(() => shownPlayMode.value?.max_party_size || 0);
  const partyTooBig = computed(() => !!partyLimit.value && partySize.value > partyLimit.value);
  const leaderName = computed(
    () => party.value?.members?.find((m) => m.id === party.value.owner_id)?.username
      || t('play.matchmaking.someone'),
  );

  const hasSelection = computed(
    () => !!shownPlayMode.value && !!shownRegion.value && shownMapNames.value.length > 0,
  );

  const canBeginMatchmaking = computed(
    () => !!party.value && hasSelection.value && !partyTooBig.value,
  );

  const summaryLabel = computed(() => {
    if (!hasSelection.value) {
      return isOwner.value
        ? t('play.matchmaking.selection_hint')
        : t('play.matchmaking.leader_choosing', { name: leaderName.value });
    }
    const total = shownMaps.value.length;
    const count = shownMapNames.value.length;
    return t('play.matchmaking.queue_summary', {
      playlist: shownPlayMode.value.friendly_name,
      maps: count === total && total > 1
        ? t('play.matchmaking.all_maps', { n: total })
        : t('play.matchmaking.map_count', count),
      region: shownRegion.value.name,
    });
  });

  const statusIsProblem = computed(
    () => !activeLobby.value && (partyTooBig.value || !!queueError.value),
  );

  const statusLabel = computed(() => {
    if (activeLobby.value) return t('play.matchmaking.match_ready');
    if (isQueued.value) return `${t('play.matchmaking.searching_players')} ${t('play.matchmaking.elapsed', { time: elapsedLabel.value })}`;
    if (partyTooBig.value) {
      return t('play.matchmaking.party_too_big', { playlist: shownPlayMode.value.friendly_name, max: partyLimit.value });
    }
    if (queueError.value) return queueError.value;
    if (!isOwner.value) return t('play.matchmaking.waiting_for_leader_named', { name: leaderName.value });
    if (partySize.value > 1) return t('play.matchmaking.party_ready', { n: partySize.value });
    return t('play.matchmaking.solo_hint');
  });

  watch([shownPlayMode, shownRegion, shownMapNames, partySize], () => {
    queueError.value = '';
  });

  function beginMatchmaking() {
    queueing.value = true;
    queueError.value = '';
    axios
      .post(`/matchmaking/parties/${party.value.id}/queue_entries`, {
        queue_entry: {
          region_id: selectedRegion.value.id,
          play_mode_ids: [selectedPlayMode.value.id],
          cs_map_names: selectedMapNames.value,
        },
        pings: measuredPings(),
      })
      .then((response) => {
        store.commit('setMatchmakingParty', { ...party.value, status: 'queued', queue_entry: response.data });
      })
      .catch((err) => {
        const errors = err.response?.data?.errors;
        queueError.value = (Array.isArray(errors) ? errors[0] : err.response?.data?.error) || t('play.matchmaking.queue_failed');
      })
      .finally(() => {
        queueing.value = false;
      });
  }

  function leaveParty() {
    const membershipId = currentMembership.value?.member_id;
    if (!membershipId) return;
    leaving.value = true;
    axios
      .delete(`/matchmaking/party_members/${membershipId}`)
      .then(() => axios.get('/matchmaking/parties'))
      .then(({ data }) => {
        store.commit('setMatchmakingParty', data.owned);
      })
      .finally(() => {
        leaving.value = false;
      });
  }

  onBeforeUnmount(() => {
    if (!selectionSyncTimer) return;
    clearTimeout(selectionSyncTimer);
    syncPartySelection();
  });

  onMounted(() => {
    Promise.allSettled([
      axios.get('/matchmaking/play_modes').then((response) => { playModes.value = response.data.play_modes; }),
      axios.get('/regions').then((response) => { regions.value = response.data.regions; }),
      loadUserPreferences(),
    ]).then(restoreSelection);
  });

  return {
    playModes,
    regions,
    party,
    queueEntry,
    activeLobby,
    isQueued,
    isOwner,
    queueing,
    leaving,
    selectorsLocked,
    lockedReason,
    shownPlayMode,
    shownRegion,
    shownMapNames,
    shownMaps,
    partySize,
    partyLimit,
    partyTooBig,
    leaderName,
    canBeginMatchmaking,
    summaryLabel,
    elapsedLabel,
    statusIsProblem,
    statusLabel,
    beginMatchmaking,
    cancelMatchmaking,
    leaveParty,
  };
}
